import {
  Flex,
  Icon,
  IconButton,
  MenuDivider,
  MenuItem,
  Tag,
  TagLeftIcon,
  Tooltip,
} from "@chakra-ui/react";
import { HamburgerIcon, WarningIcon } from "@chakra-ui/icons";
import {
  MdAdminPanelSettings,
  MdComment,
  MdEmail,
  MdFolder,
  MdSettings,
} from "react-icons/md";
import { useApolloClient } from "@apollo/client";
import { Feature } from "__generated__/gql/graphql";
import {
  useCurrentBrandData,
  useCurrentUserData,
  useIsInvalidSubscription,
  useUserHasFeature,
} from "utils/currentUserHooks";
import SwitchAccountMenuItem from "components/Core/Auth/SwitchAccount/MenuItem";
import {
  ADMIN_INDEX,
  ANALYTICS_DATA_SOURCES,
  ANALYTICS_INDEX,
  BRAND_BILLING,
  BRAND_SETTINGS,
  COMMENTS_INDEX,
  DOCUMENTS_INDEX,
  EMAIL_INBOX_INDEX,
  PRODUCTS_INDEX,
} from "utils/links";
import { asyncVoid } from "utils/asyncVoidHandler";
import { APP_NAME } from "utils/config";
import {
  BRAND_LAYOUT_HOMEPAGES,
  useIsAnalyticsLayoutPath,
} from "components/Layout/brandLayoutUtils";
import TooltipIconLinkButton from "components/Layout/NavBar/TooltipIconLinkButton";
import { COMMENTS_TITLE } from "components/Comments/utils";
import BrandName from "components/Shared/BrandName";
import Link from "components/Shared/Link";
import NavBarContainer from "./Container";
import MenuItemLink from "./MenuItemLink";
import NavBarLogo from "./Logo";
import NavBarAccountMenu from "./AccountMenu";
import NavBarLink from "./NavBarLink";

type NavBarProps = {
  handleShowOrHideSidebar: () => void;
  isMobileSideBarHidden?: boolean;
  isMobileSideBarOpen: boolean;
};

export default function NavBar({
  handleShowOrHideSidebar,
  isMobileSideBarHidden,
  isMobileSideBarOpen,
}: NavBarProps) {
  const client = useApolloClient();
  const userData = useCurrentUserData();
  const brandData = useCurrentBrandData();

  const isAnalyticsLayout = useIsAnalyticsLayoutPath();
  const isInvalidSubscription = useIsInvalidSubscription();

  const isBrandInboxEnabled = useUserHasFeature(Feature.BRAND_INBOX);

  return (
    <NavBarContainer id="NavBar">
      <NavBarLogo
        href={
          brandData?.layout
            ? BRAND_LAYOUT_HOMEPAGES[brandData.layout]
            : undefined
        }
      />

      {isInvalidSubscription && (
        <Tooltip
          hasArrow
          label={
            <>
              The subscription for <BrandName /> is invalid. Access to most
              features is disabled. Go to your subscription settings to learn
              more.
            </>
          }
        >
          <Link href={BRAND_BILLING} alignSelf="center">
            <Tag colorScheme="orange">
              <TagLeftIcon as={WarningIcon} />
              Invalid Subscription
            </Tag>
          </Link>
        </Tooltip>
      )}

      {isAnalyticsLayout && (
        <>
          <NavBarLink
            href={ANALYTICS_INDEX}
            label="Analytics"
            display={{ base: "none", md: "flex" }}
          />
          <NavBarLink
            href={ANALYTICS_DATA_SOURCES}
            label="Data Sources"
            exactPathname={false}
          />
          <NavBarLink href={PRODUCTS_INDEX} label="Products" />
        </>
      )}

      <Flex display={{ base: "none", md: "flex" }} marginLeft="auto">
        {isAnalyticsLayout === false && (
          <>
            <TooltipIconLinkButton
              href={COMMENTS_INDEX}
              label={COMMENTS_TITLE}
              icon={<Icon as={MdComment} />}
            />
            {isBrandInboxEnabled && (
              <TooltipIconLinkButton
                href={EMAIL_INBOX_INDEX}
                label="Email Inbox"
                icon={<Icon as={MdEmail} />}
              />
            )}
            <TooltipIconLinkButton
              href={DOCUMENTS_INDEX}
              label="Document Library"
              icon={<Icon as={MdFolder} />}
            />
          </>
        )}

        <NavBarAccountMenu
          label={brandData?.name}
          image={userData?.image}
          isLoading={!userData}
        >
          <MenuItem
            as={MenuItemLink}
            href={BRAND_SETTINGS}
            icon={<MdSettings />}
          >
            Account Settings
          </MenuItem>
          <SwitchAccountMenuItem />
          {userData?.isStaff && (
            <>
              <MenuDivider />
              <MenuItem
                as={MenuItemLink}
                href={ADMIN_INDEX}
                icon={<MdAdminPanelSettings />}
                onClick={() => {
                  asyncVoid(client.clearStore());
                }}
              >
                {APP_NAME} Admin
              </MenuItem>
            </>
          )}
          <MenuDivider />
        </NavBarAccountMenu>
      </Flex>

      {!isMobileSideBarHidden && (
        <IconButton
          icon={<HamburgerIcon boxSize={7} />}
          variant="ghost"
          display={{ md: "none" }}
          borderRadius="md"
          p={2}
          color="magenta.dark"
          aria-label="Open or close side bar menu"
          aria-expanded={isMobileSideBarOpen}
          aria-controls="SideBar"
          _expanded={{ boxShadow: "inner", bg: "gray.50" }}
          marginLeft="auto"
          marginRight={2}
          alignSelf="center"
          onClick={handleShowOrHideSidebar}
        />
      )}
    </NavBarContainer>
  );
}
